// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Container, CssBaseline, ThemeProvider } from '@mui/material';
import theme from './theme';
import Navigation from './components/Navigation';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import FacilityClientPage from './pages/ClientEtablissementPage';
import BillingPage from './pages/FacturationPage';
import ServersPage from './pages/ServeursPage';
import InstallKeysPage from './pages/ClesDePosePage';
import InstallAccountsPage from './pages/ComptesDePosePage';
import PaymentPage from './pages/PaymentPage';
import ReservationsPage from './pages/ReservationsPage';
import ProspectionPage from './pages/ProspectionPage';
import SettingsPage from './pages/SettingsPage';
import { SESSION_KEY } from './utils/api';
import './App.css';

function ProtectedRoute({ children }) {
  const token = localStorage.getItem(SESSION_KEY);
  if (!token) return <Navigate to="/login" replace />;
  return (
    <>
      <Navigation />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {children}
      </Container>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

          {/* La liste mêle les deux natures de client ; la fiche dépend du
              modèle qui porte le client : une organisation pour un
              établissement, un `Client` pour un foyer. Les routes ne se
              recouvrent pas : `/clients/:id` ne couvre qu'un segment. */}
          <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
          <Route path="/clients/etablissement/:id"
            element={<ProtectedRoute><FacilityClientPage /></ProtectedRoute>} />
          <Route path="/clients/:id" element={<ProtectedRoute><ClientDetailPage /></ProtectedRoute>} />

          {/* Réservations du kit et demandes d'intérêt des particuliers (vigeos.fr). */}
          <Route path="/reservations" element={<ProtectedRoute><ReservationsPage /></ProtectedRoute>} />
          {/* Présentation envoyée aux établissements démarchés par téléphone. */}
          <Route path="/prospection" element={<ProtectedRoute><ProspectionPage /></ProtectedRoute>} />
          <Route path="/facturation" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
          <Route path="/serveurs" element={<ProtectedRoute><ServersPage /></ProtectedRoute>} />
          {/* Route sans paramètre : l'établissement voyage dans l'état du
              routeur (`state`), jamais dans l'URL. Aucune donnée d'un ordre de
              fabrication ne passe par une URL, qui finirait dans l'historique,
              le journal nginx et chez Cloudflare. */}
          <Route path="/cles-de-pose" element={<ProtectedRoute><InstallKeysPage /></ProtectedRoute>} />
          {/* Comptes des poseurs, valables chez tous les clients. Entrée
              distincte des clés de pose : l'une fabrique l'identité d'une
              machine, l'autre celle d'une personne qui entre chez les
              clients. */}
          <Route path="/comptes-de-pose" element={<ProtectedRoute><InstallAccountsPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/pay/:token" element={<PaymentPage />} />

          {/* Adresses d'écrans fusionnés, encore présentes dans des favoris et
              des courriels internes. Elles mènent à l'écran qui porte
              l'information ; un renvoi à l'accueil ferait croire à une
              panne. */}
          <Route path="/etablissements" element={<Navigate to="/clients" replace />} />
          <Route path="/properties" element={<Navigate to="/clients" replace />} />
          <Route path="/kits" element={<Navigate to="/serveurs" replace />} />
          {/* L'état en ligne et la détection de chaque serveur se lisent sur le
              tableau de bord ; la liste des serveurs porte le reste. */}
          <Route path="/fleet" element={<Navigate to="/serveurs" replace />} />

          {/* Repli général, y compris pour les liens /schedule/:token envoyés
              par courriel : la prise de rendez-vous d'installation ne fait pas
              partie du produit et le backend refuse leurs jetons. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
